/**
 * SurveyAdmin — Admin view for reading survey submissions inside the LMS.
 * Mounted as a tab in AdminDashboard.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { config } from '../config';
import { boardDirectorQuestions } from './boardDirectorQuestions';
import { boardDirectorFollowupQuestions } from './boardDirectorFollowupQuestions';
import { birthdayPartyBookingQuestions } from './birthdayPartyBookingQuestions';
import type { SurveyQuestion } from './types';
import './SurveyAdmin.css';

// ── Types ──────────────────────────────────────────────────────────────────

interface SurveyResponseItem {
  id: string;
  question_id: string;
  answer_text: string | null;
  answer_json: string | null;
}

interface SurveySubmission {
  id: string;
  survey_slug: string;
  respondent_name: string | null;
  respondent_email: string | null;
  submitted_at: string;
  responses: SurveyResponseItem[];
}

// ── Survey question registry — maps slug → ordered questions ──────────────

const SURVEY_QUESTIONS: Record<string, SurveyQuestion[]> = {
  'board-director':          boardDirectorQuestions,
  'board-director-followup': boardDirectorFollowupQuestions,
  'birthday-party-booking':  birthdayPartyBookingQuestions,
};

function getQuestionDef(slug: string, questionId: string): SurveyQuestion | undefined {
  const questions = SURVEY_QUESTIONS[slug];
  if (!questions) return undefined;
  return questions.find((q) => q.id === questionId);
}

function getFullAnswerLabel(question: SurveyQuestion | undefined, value: string): string {
  if (!question?.options) return value;
  const opt = question.options.find((o) => o.value === value);
  return opt ? opt.label : value;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Vancouver',
  });
}

// ── Sub-component: single submission detail ────────────────────────────────

const SubmissionDetail: React.FC<{
  sub: SurveySubmission;
  onClose: () => void;
  onDeleted: (id: string) => void;
}> = ({ sub, onClose, onDeleted }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { getValidToken } = await import('../utils/tokenRefresh');
      const token = await getValidToken();
      await axios.delete(
        `${config.BACKEND_URL}/api/surveys/submission/${sub.id}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      onDeleted(sub.id);
      onClose();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete submission.');
      setDeleting(false);
    }
  };

  const surveyQuestions = SURVEY_QUESTIONS[sub.survey_slug] || [];
  const questionOrder = surveyQuestions.map((q) => q.id);

  const sorted = useMemo(() => {
    const responseMap = new Map(sub.responses.map((r) => [r.question_id, r]));
    const ordered: SurveyResponseItem[] = [];
    for (const qId of questionOrder) {
      const r = responseMap.get(qId);
      if (r) ordered.push(r);
    }
    for (const r of sub.responses) {
      if (!questionOrder.includes(r.question_id)) ordered.push(r);
    }
    return ordered;
  }, [sub.responses, questionOrder]);

  return (
    <div className="sa-modal-overlay" onClick={onClose}>
      <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sa-modal-header">
          <div>
            <div className="sa-modal-slug">{sub.survey_slug}</div>
            <h2 className="sa-modal-title">
              {sub.respondent_name || 'Anonymous respondent'}
            </h2>
            <div className="sa-modal-meta">
              {sub.respondent_email && <span>{sub.respondent_email} · </span>}
              <span>{formatDate(sub.submitted_at)}</span>
            </div>
          </div>
          <button className="sa-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="sa-modal-body">
          {sorted.map((r) => {
            const qDef = getQuestionDef(sub.survey_slug, r.question_id);
            const questionLabel = qDef?.label || r.question_id;
            const isTextAnswer = qDef?.type === 'textarea' || qDef?.type === 'text' || qDef?.type === 'email';

            let answerDisplay: React.ReactNode;
            if (r.answer_json) {
              try {
                const arr: string[] = JSON.parse(r.answer_json);
                answerDisplay = (
                  <ul className="sa-detail-list">
                    {arr.map((v, i) => (
                      <li key={i}>{getFullAnswerLabel(qDef, v)}</li>
                    ))}
                  </ul>
                );
              } catch {
                answerDisplay = <p className="sa-detail-answer-text">{r.answer_json}</p>;
              }
            } else if (r.answer_text) {
              if (isTextAnswer) {
                answerDisplay = <p className="sa-detail-answer-text">{r.answer_text}</p>;
              } else {
                answerDisplay = (
                  <p className="sa-detail-answer-selected">{getFullAnswerLabel(qDef, r.answer_text)}</p>
                );
              }
            } else {
              answerDisplay = <p className="sa-detail-answer-empty">No answer provided</p>;
            }

            return (
              <div key={r.id} className="sa-detail-qa">
                <div className="sa-detail-question">{questionLabel}</div>
                {answerDisplay}
              </div>
            );
          })}

          <section className="sa-section sa-meta-section">
            <div className="sa-meta-row"><span>Submission ID</span><code>{sub.id}</code></div>
            <div className="sa-meta-row"><span>Survey</span><code>{sub.survey_slug}</code></div>
            <div className="sa-meta-row"><span>Submitted</span><span>{formatDate(sub.submitted_at)}</span></div>
          </section>

          <section className="sa-section sa-delete-section">
            {!confirmDelete ? (
              <button className="sa-delete-btn" onClick={() => setConfirmDelete(true)}>
                🗑 Delete this submission
              </button>
            ) : (
              <div className="sa-delete-confirm">
                <p className="sa-delete-warn">
                  Permanently delete this submission and all its answers? This cannot be undone.
                </p>
                <div className="sa-delete-actions">
                  <button
                    className="sa-delete-confirm-btn"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting…' : 'Yes, delete'}
                  </button>
                  <button
                    className="sa-delete-cancel-btn"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

const SURVEY_SLUGS = [
  { value: 'all',                      label: 'All surveys' },
  { value: 'board-director',           label: 'Board Director Survey' },
  { value: 'board-director-followup',  label: 'Board Director Follow-Up' },
  { value: 'birthday-party-booking',   label: 'Birthday Party Booking Requests' },
];

const SurveyAdmin: React.FC = () => {
  const [submissions, setSubmissions]     = useState<SurveySubmission[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [slugFilter, setSlugFilter]       = useState('all');
  const [selected, setSelected]           = useState<SurveySubmission | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting]           = useState(false);

  const handleDeleted = useCallback((id: string) => {
    setSubmissions((prev) => prev.filter((s) => s.id !== id));
    setConfirmDeleteId(null);
  }, []);

  const handleRowDelete = useCallback(async (id: string) => {
    setDeleting(true);
    try {
      const { getValidToken } = await import('../utils/tokenRefresh');
      const token = await getValidToken();
      await axios.delete(
        `${config.BACKEND_URL}/api/surveys/submission/${id}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      handleDeleted(id);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete submission.');
    } finally {
      setDeleting(false);
    }
  }, [handleDeleted]);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { getValidToken } = await import('../utils/tokenRefresh');
      const token   = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const url =
        slugFilter === 'all'
          ? `${config.BACKEND_URL}/api/surveys`
          : `${config.BACKEND_URL}/api/surveys/${slugFilter}`;

      const res = await axios.get(url, { headers });
      setSubmissions(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load survey submissions.');
    } finally {
      setLoading(false);
    }
  }, [slugFilter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  return (
    <div className="sa-container">
      {/* Header */}
      <div className="sa-header">
        <div>
          <h2 className="sa-title">📋 Survey Responses</h2>
          <p className="sa-subtitle">
            {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
            {slugFilter !== 'all' ? ` · ${SURVEY_SLUGS.find(s => s.value === slugFilter)?.label}` : ''}
          </p>
        </div>
        <div className="sa-header-actions">
          <select
            className="sa-select"
            value={slugFilter}
            onChange={(e) => setSlugFilter(e.target.value)}
          >
            {SURVEY_SLUGS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button className="sa-refresh-btn" onClick={fetchSubmissions} disabled={loading}>
            {loading ? '…' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {/* States */}
      {loading && <div className="sa-state">Loading submissions…</div>}
      {error   && <div className="sa-state sa-state-error">{error}</div>}

      {/* Table */}
      {!loading && !error && submissions.length === 0 && (
        <div className="sa-state sa-state-empty">No submissions yet.</div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Survey</th>
                <th>Respondent</th>
                <th>Submitted</th>
                <th>Answers</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((sub, i) => (
                <tr key={sub.id} className={`sa-row${confirmDeleteId === sub.id ? ' sa-row-confirm' : ''}`}>
                  <td className="sa-num">{submissions.length - i}</td>
                  <td><span className="sa-slug-badge">{sub.survey_slug}</span></td>
                  <td>
                    <div className="sa-respondent">
                      {sub.respondent_name || <em>Anonymous</em>}
                    </div>
                    {sub.respondent_email && (
                      <div className="sa-respondent-email">{sub.respondent_email}</div>
                    )}
                  </td>
                  <td className="sa-date">{formatDate(sub.submitted_at)}</td>
                  <td className="sa-answer-count">{sub.responses.length} answers</td>
                  <td className="sa-row-actions">
                    {confirmDeleteId === sub.id ? (
                      <div className="sa-row-confirm-wrap">
                        <span className="sa-row-confirm-label">Delete?</span>
                        <button
                          className="sa-row-confirm-yes"
                          onClick={() => handleRowDelete(sub.id)}
                          disabled={deleting}
                        >
                          {deleting ? '…' : 'Yes'}
                        </button>
                        <button
                          className="sa-row-confirm-no"
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={deleting}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <div className="sa-row-btns">
                        <button className="sa-view-btn" onClick={() => setSelected(sub)}>
                          View
                        </button>
                        <button
                          className="sa-row-delete-btn"
                          onClick={() => setConfirmDeleteId(sub.id)}
                          title="Delete submission"
                        >
                          🗑
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <SubmissionDetail
          sub={selected}
          onClose={() => setSelected(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default SurveyAdmin;
